// Path: src/index.tsx
// Added HMR handling to prevent duplicate createRoot, enhanced error logging
// Optimized for macOS 11 and mobile responsiveness
// 06/07/25 3:45 PM +07
import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "mobx-react";
import { UserStoreProvider } from "./stores/UserStore";
import { FirebaseProvider } from "./components/dev/Firebase/Firebase";
import { TimerProvider } from "./context/TimerContext";
import Routes from "./routes/Routes";
import { AbilityContext, ability } from "./ability";
import { HelmetProvider } from "react-helmet-async";
import "react-toastify/dist/ReactToastify.min.css";
import "./styles/global.css";

const LazyToastContainer = React.lazy(() =>
  import("react-toastify").then((module) => ({
    default: module.ToastContainer,
  }))
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <HelmetProvider>
        <UserStoreProvider>
          <Provider>
            <AbilityContext.Provider value={ability}>
              <Routes />
              <React.Suspense fallback={<div>Loading notifications...</div>}>
                <LazyToastContainer
                  autoClose={4000}
                  hideProgressBar={false}
                  closeOnClick
                  pauseOnHover
                  draggable
                  style={{ zIndex: 9999 }}
                />
              </React.Suspense>
            </AbilityContext.Provider>
          </Provider>
        </UserStoreProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
};

const AppWrapper: React.FC = () => (
  <FirebaseProvider>
    <TimerProvider>
      <App />
    </TimerProvider>
  </FirebaseProvider>
);

let root: ReturnType<typeof createRoot> | ReturnType<typeof hydrateRoot> | null = null;

function renderApp() {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    document.body.innerHTML = "<h1>Error: Root element not found</h1>";
    return;
  }

  if (!root) {
    root = createRoot(rootElement);
    console.log("Root created:", !!root);
  }

  console.log("Starting render...");
  root.render(<AppWrapper />);
  console.log("Render completed");
}

window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
});

if (import.meta.hot) {
  import.meta.hot.accept();
  import.meta.hot.dispose(() => {
    if (root) root.unmount();
    root = null;
  });
}

renderApp();